import type { ReactNode } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// Modal state
interface ModalState {
  children: ReactNode | null;
  closable: boolean;
  onClose?: () => void;
  onValidate?: () => void;
}

interface UIState {
  // Modal state
  modal: ModalState;
  openModal: (
    modal: Omit<ModalState, "children"> & { children: ReactNode }
  ) => void;
  closeModal: (validated?: boolean) => void;

  // Sidebar state
  displaySidenav: boolean;
  toggleSidenav: () => void;
  setSidenav: (visible: boolean) => void;

  // Scroll position
  scrollYPosition: number;
  setScrollYPosition: (position: number) => void;

  // Mail sorting preference
  isMailsAreSortedByColors: boolean;
  toggleMailsSortByColors: () => void;

  // Help steps visibility
  isHelpStepsShown: boolean;
  setHelpStepsShown: (shown: boolean) => void;

  // Tutorial modal
  isTutorialOpen: boolean;
  openTutorial: () => void;
  closeTutorial: () => void;

  // Today's date (for relative date calculations)
  todayDate: Date;
  setTodayDate: (date: Date) => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Mail filter
  mailFilter: "all" | "important" | "sent" | "drafts";
  setMailFilter: (filter: "all" | "important" | "sent" | "drafts") => void;

  // Search query
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Modal
      modal: { children: null, closable: false },
      openModal: (modal) => set({ modal }),
      closeModal: (validated = false) => {
        const { modal } = get();
        if (!validated && modal.onClose) modal.onClose();
        if (validated && modal.onValidate) modal.onValidate();
        set({ modal: { children: null, closable: false } });
      },

      // Sidebar
      displaySidenav: true,
      toggleSidenav: () =>
        set((state) => ({ displaySidenav: !state.displaySidenav })),
      setSidenav: (visible) => set({ displaySidenav: visible }),

      // Scroll position
      scrollYPosition: 0,
      setScrollYPosition: (position) => set({ scrollYPosition: position }),
      // Mail sorting
      isMailsAreSortedByColors: false,
      toggleMailsSortByColors: () =>
        set((state) => ({
          isMailsAreSortedByColors: !state.isMailsAreSortedByColors,
        })),

      // Help steps
      isHelpStepsShown: false,
      setHelpStepsShown: (shown) => set({ isHelpStepsShown: shown }),

      // Tutorial modal
      isTutorialOpen: false,
      openTutorial: () => set({ isTutorialOpen: true }),
      closeTutorial: () => set({ isTutorialOpen: false }),

      // Today's date
      todayDate: new Date(),
      setTodayDate: (date) => set({ todayDate: date }),

      // Loading
      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),

      // Mail filter
      mailFilter: "all",
      setMailFilter: (filter) => set({ mailFilter: filter }),

      // Search query
      searchQuery: "",
      setSearchQuery: (query) => set({ searchQuery: query }),
    }),
    {
      name: "inbasket-ui-storage",
      storage: createJSONStorage(() => sessionStorage),
      // Exclude modal and runtime state from persistence
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) =>
              !["modal", "isLoading", "searchQuery", "isTutorialOpen"].includes(
                key
              )
          )
        ) as UIState,
    }
  )
);

export default useUIStore;
